import React from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';

interface SocialMediaLimit {
  name: string;
  limit: number;
  icon: string;
}

const SOCIAL_LIMITS: SocialMediaLimit[] = [
  { name: 'Twitter/X Post', limit: 280, icon: '𝕏' },
  { name: 'Instagram Bio', limit: 150, icon: '📷' },
  { name: 'TikTok Bio', limit: 80, icon: '🎵' },
  { name: 'TikTok Username', limit: 24, icon: '🎵' },
  { name: 'Discord Message', limit: 2000, icon: '💬' },
  { name: 'YouTube Title', limit: 100, icon: '▶️' },
  { name: 'Facebook Post', limit: 63206, icon: '📘' },
  { name: 'LinkedIn Post', limit: 3000, icon: '💼' },
];

interface CharacterCounterProps {
  text: string;
  maxChars: number;
}

export function CharacterCounter({ text, maxChars }: CharacterCounterProps) {
  const charCount = text.length;
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const lineCount = text ? text.split('\n').length : 0;
  const uniqueChars = new Set(text).size;

  // Calculate percentage for main counter
  const percentage = Math.min((charCount / maxChars) * 100, 100);
  const isNearLimit = percentage > 80;
  const isAtLimit = percentage >= 100;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`text-sm cursor-pointer hover:underline transition-colors ${
            isAtLimit
              ? 'text-destructive font-medium'
              : isNearLimit
                ? 'text-amber-500'
                : 'text-muted-foreground'
          }`}
        >
          {charCount}/{maxChars} characters
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm mb-2">Text Statistics</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Characters:</span>
                <span className="font-mono">{charCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Words:</span>
                <span className="font-mono">{wordCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lines:</span>
                <span className="font-mono">{lineCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unique chars:</span>
                <span className="font-mono">{uniqueChars}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-sm mb-2">Social Media Limits</h4>
            <div className="space-y-2">
              {SOCIAL_LIMITS.slice(0, 5).map((platform) => {
                const withinLimit = charCount <= platform.limit;
                const platformPercentage = Math.min((charCount / platform.limit) * 100, 100);

                return (
                  <div key={platform.name} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1">
                        <span>{platform.icon}</span>
                        <span>{platform.name}</span>
                      </span>
                      <span className={withinLimit ? 'text-green-500' : 'text-destructive'}>
                        {charCount}/{platform.limit}
                      </span>
                    </div>
                    <Progress
                      value={platformPercentage}
                      className={`h-1.5 ${
                        !withinLimit ? '[&>div]:bg-destructive' :
                        platformPercentage > 80 ? '[&>div]:bg-amber-500' : ''
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-xs text-muted-foreground pt-2 border-t">
            Click to view detailed character statistics
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
